<script setup lang="ts">
import { computed, onBeforeUnmount, ref, nextTick } from 'vue'
import { useI18n } from 'vue-i18n'
import {
  mdiCloseCircle,
  mdiCalendarBlankOutline,
  mdiCheckCircleOutline,
  mdiCloseCircleOutline,
  mdiClockOutline,
  mdiCheckCircle,
} from '@mdi/js'

type EveLinkStatus =
  | 'disabled'
  | 'enabled'
  | 'regular'
  | 'expiring'
  | 'expired'
  | 'pending_setup'

const props = withDefaults(
  defineProps<{
    status: EveLinkStatus
    activatedAt?: string
    activeLinksCount?: number
    expiresAt?: string
    context?: 'account' | 'contact'
    requestLabels?: string[]
  }>(),
  {
    expiresAt: undefined,
    activatedAt: undefined,
    context: 'account',
    activeLinksCount: undefined,
    requestLabels: () => [],
  }
)

const { t } = useI18n()

const STATUS_UI = {
  enabled: {
    labelKey: 'eveLink.status.enabled',
    tooltipKey: 'eveLink.status.enabled',
    icon: mdiCheckCircle,
    tone: 'success',
  },
  regular: {
    labelKey: 'eveLink.status.regular',
    tooltipKey: 'eveLink.status.regular',
    icon: mdiCheckCircleOutline,
    tone: 'success',
  },
  expiring: {
    labelKey: 'eveLink.status.expiring',
    tooltipKey: 'eveLink.status.expiring',
    icon: mdiCalendarBlankOutline,
    tone: 'warning',
  },
  expired: {
    labelKey: 'eveLink.status.expired',
    tooltipKey: 'eveLink.status.expired',
    icon: mdiCloseCircleOutline,
    tone: 'error',
  },
  disabled: {
    labelKey: 'eveLink.status.disabled',
    tooltipKey: 'eveLink.status.disabled',
    icon: mdiCloseCircle,
    tone: 'error',
  },
  pending_setup: {
    labelKey: 'eveLink.status.pendingSetup',
    tooltipKey: 'eveLink.status.pendingSetup',
    icon: mdiClockOutline,
    tone: 'warning',
  },
} as const

const ui = computed(() => STATUS_UI[props.status])

const statusLabel = computed(() => t(ui.value.labelKey))

function prettifyFallbackLabel(label: string) {
  const normalized = String(label ?? '')
    .trim()
    .toUpperCase()

  return normalized
    .toLowerCase()
    .split('_')
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ')
}

const normalizedRequestLabels = computed(() => {
  return props.requestLabels
    .filter(Boolean)
    .map((label) => prettifyFallbackLabel(label))
})

const requestLabelsText = computed(() => {
  return normalizedRequestLabels.value.join(' • ')
})

const toastText = computed(() => {
  if (props.status === 'enabled' || props.status === 'regular') {
    return t(ui.value.tooltipKey, {
      dateActivate: props.activatedAt ?? '00/00/0000',
      totalActivate: props.activeLinksCount ?? 0,
      startDate: props.activatedAt ?? '00/00/0000',
      endDate: props.expiresAt ?? '00/00/0000',
    })
  }

  if (props.status === 'expiring' || props.status === 'expired') {
    return t(ui.value.tooltipKey, {
      startDate: props.activatedAt ?? '00/00/0000',
      endDate: props.expiresAt ?? '00/00/0000',
    })
  }

  return t(ui.value.tooltipKey)
})

const indicatorEl = ref<HTMLElement | null>(null)
const toastEl = ref<HTMLElement | null>(null)
const showToast = ref(false)

const pos = ref({ top: 0, left: 0 })
const GAP = 8

function clamp(n: number, min: number, max: number) {
  return Math.max(min, Math.min(max, n))
}

function updateToastPosition() {
  const host = indicatorEl.value
  const toast = toastEl.value
  if (!host || !toast) return

  const r = host.getBoundingClientRect()
  const tr = toast.getBoundingClientRect()

  let left = r.left + r.width / 2 - tr.width / 2
  left = clamp(left, 8, window.innerWidth - tr.width - 8)

  let top = r.top - tr.height - GAP
  if (top < 8) top = r.bottom + GAP

  pos.value = { top, left }
}

function openToast() {
  if (showToast.value) return

  showToast.value = true

  nextTick(() => {
    updateToastPosition()
    window.addEventListener('scroll', updateToastPosition, true)
    window.addEventListener('resize', updateToastPosition)
  })
}

function closeToast() {
  showToast.value = false
  window.removeEventListener('scroll', updateToastPosition, true)
  window.removeEventListener('resize', updateToastPosition)
}

onBeforeUnmount(() => {
  window.removeEventListener('scroll', updateToastPosition, true)
  window.removeEventListener('resize', updateToastPosition)
})
</script>

<template>
  <div class="status-indicator-wrap">
    <div
      ref="indicatorEl"
      class="status-indicator"
      :class="`status-indicator--${ui.tone}`"
      role="status"
      aria-live="polite"
      tabindex="0"
      @mouseenter="openToast"
      @mouseleave="closeToast"
      @focus="openToast"
      @blur="closeToast"
    >
      <svg
        class="status-indicator__icon"
        viewBox="0 0 24 24"
        aria-hidden="true"
      >
        <path :d="ui.icon" />
      </svg>

      <span class="status-indicator__label">
        {{ statusLabel }}
      </span>

      <span v-if="requestLabelsText" class="status-indicator__requests">
        • {{ requestLabelsText }}
      </span>

      <Teleport to="body">
        <div
          v-if="showToast"
          ref="toastEl"
          class="eve-toast"
          role="status"
          aria-live="polite"
          :style="{ top: `${pos.top}px`, left: `${pos.left}px` }"
        >
          {{ toastText }}
        </div>
      </Teleport>
    </div>
  </div>
</template>

<style scoped>
.status-indicator-wrap {
  display: inline-flex;
  align-items: center;
}

.status-indicator {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  line-height: 20px;
  font-weight: 500;
  flex-wrap: wrap;
}

.status-indicator__icon {
  width: 16px;
  height: 16px;
  fill: currentColor;
}

.status-indicator--success {
  color: var(--color-success, #2e7d32);
}

.status-indicator--warning {
  color: var(--color-warning, #ed6c02);
}

.status-indicator--error {
  color: var(--color-error, #d32f2f);
}

.status-indicator__label {
  white-space: nowrap;
}

.status-indicator__requests {
  color: rgba(0, 0, 0, 0.6);
  font-size: 13px;
  line-height: 20px;
  font-weight: 400;
  white-space: nowrap;
}

@media (max-width: 959.98px) {
  .status-indicator {
    gap: 6px;
  }

  .status-indicator__icon {
    width: 14px;
    height: 14px;
  }

  .status-indicator__label,
  .status-indicator__requests {
    font-size: 12px;
    line-height: 16px;
  }
}
</style>

<style>
.eve-toast {
  position: fixed;
  z-index: 9999;
  max-width: min(520px, calc(100vw - 16px));
  padding: 12px 14px;
  border-radius: 10px;
  background: rgba(35, 35, 35, 0.92);
  color: #fff;
  font-size: 14px;
  line-height: 20px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.25);
  pointer-events: none;
}
</style>
